import store from "../store";
import { sessionChanged } from "../store/actions/authSessionAction";

const storageName = "authData";
const createSessionId = () => {
  const bytes = new Uint32Array(4);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (part) => part.toString(16).padStart(8, "0")).join("");
};

export const createAuthSession = ({ storage, authStore, eventTarget }) => {
  let snapshot = { token: null, sessionId: null, checkDone: false };
  const listeners = new Set();
  const read = () => {
    try {
      const data = JSON.parse(storage.getItem(storageName));
      if (!data || typeof data.token !== "string" || !data.token.trim()) return null;
      if (typeof data.sessionId !== "string" || !data.sessionId) {
        // Persist the migration once so other tabs adopt the same identity.
        data.sessionId = createSessionId();
        storage.setItem(storageName, JSON.stringify({ token: data.token, sessionId: data.sessionId }));
      }
      return { token: data.token, sessionId: data.sessionId };
    } catch {
      return null;
    }
  };
  const publish = (data, external) => {
    const next = { token: data?.token || null, sessionId: data?.sessionId || null, checkDone: true };
    if (snapshot.checkDone && snapshot.token === next.token && snapshot.sessionId === next.sessionId)
      return snapshot;
    const changed = snapshot.sessionId !== next.sessionId || !snapshot.checkDone;
    snapshot = next;
    if (changed) authStore.dispatch(sessionChanged(next.sessionId, external));
    listeners.forEach((listener) => listener());
    return snapshot;
  };
  const syncFromStorage = () => publish(read(), true);
  const onStorage = (event) => {
    if (event.key === storageName || event.key === null) syncFromStorage();
  };
  const subscribe = (listener) => {
    if (!listeners.size) {
      eventTarget?.addEventListener("storage", onStorage);
      eventTarget?.addEventListener("focus", syncFromStorage);
      eventTarget?.addEventListener("pageshow", syncFromStorage);
    }
    listeners.add(listener);
    syncFromStorage();
    return () => {
      listeners.delete(listener);
      if (!listeners.size) {
        eventTarget?.removeEventListener("storage", onStorage);
        eventTarget?.removeEventListener("focus", syncFromStorage);
        eventTarget?.removeEventListener("pageshow", syncFromStorage);
      }
    };
  };
  const startSession = (token) => {
    if (typeof token !== "string" || !token.trim()) throw new Error("Missing access token");
    const data = { token, sessionId: createSessionId() };
    storage.setItem(storageName, JSON.stringify(data));
    publish(data, false);
  };
  const endSession = () => {
    storage.removeItem(storageName);
    publish(null, false);
  };
  const replaceAccessToken = (expectedSessionId, expectedToken, nextToken) => {
    const current = syncFromStorage();
    if (!expectedSessionId || current.sessionId !== expectedSessionId || current.token !== expectedToken)
      return false;
    if (typeof nextToken !== "string" || !nextToken.trim()) throw new Error("Missing access token");
    const data = { token: nextToken, sessionId: expectedSessionId };
    storage.setItem(storageName, JSON.stringify(data));
    publish(data, false);
    return true;
  };
  publish(read(), false);
  return {
    getSnapshot: () => snapshot,
    subscribe,
    syncFromStorage,
    startSession,
    endSession,
    replaceAccessToken,
  };
};

const authSession = createAuthSession({ storage: localStorage, authStore: store, eventTarget: window });
export default authSession;
